/**
 * Zwevende contactwidget rechtsonder.
 *
 * Komt na een seconde in beeld: de widget schuift omhoog en fadet in, daarna de foto en
 * de tekstbubbels er één voor één achteraan. Klikken op de tekst of op de foto wisselt de
 * bubbels om voor de drie contactknoppen; nog een klik draait het terug.
 *
 * De beginstand wordt door JS gezet en niet in de CSS: zonder JavaScript hoort de widget
 * er gewoon te staan. De tooltips op de knoppen zijn puur CSS.
 *
 * De widget wijkt voor twee dingen. Een venster dat de hoek opeist meldt zich met een
 * overlay:toggle-bericht op document -- het paneel met de toelichtingen bij de
 * arbeidsvoorwaarden doet dat. En een sectie die de contactpersoon zelf al toont, zoals
 * het sollicitatieblok, zet er data-contact-widget-hide op; zolang die in beeld is blijft
 * de widget weg. Twee keer dezelfde Henk in beeld is er een te veel.
 *
 * Allebei melden zich als opeiser, en pas als er geen enkele meer over is komt de widget
 * terug. Zonder dat zou het sluiten van het paneel de widget terugroepen terwijl je nog
 * midden in het sollicitatieblok staat.
 */

import gsap from 'gsap';

const DELAY = 1;

export default function initContactWidget() {
  document.querySelectorAll('[data-contact-widget]').forEach(setup);
}

/**
 * Wegschuiven en terughalen. Dezelfde afstand als de intro (y: 24), zodat de widget
 * dezelfde kant op verdwijnt als waar hij vandaan kwam. autoAlpha zet ook visibility op
 * hidden, dus onderweg vangt hij geen klikken meer op.
 *
 * overwrite: draait de intro nog, dan wint dit -- anders komt de widget een tel later
 * alsnog in beeld over het paneel heen.
 *
 * @param {Element} widget
 * @param {boolean} verborgen
 */
function wijk(widget, verborgen) {
  gsap.to(widget, {
    autoAlpha: verborgen ? 0 : 1,
    y: verborgen ? 24 : 0,
    duration: 0.35,
    ease: 'power3.out',
    overwrite: true,
  });
}

function setup(widget) {
  const toggles = Array.from(widget.querySelectorAll('[data-contact-widget-toggle]'));
  const bubbleWrap = widget.querySelector('[data-contact-widget-bubbles]');
  const bubbles = Array.from(widget.querySelectorAll('[data-contact-widget-bubble]'));
  const actions = widget.querySelector('[data-contact-widget-actions]');
  const buttons = actions ? Array.from(actions.querySelectorAll('[data-contact-widget-action]')) : [];
  const photo = widget.querySelector('.contact-widget__photo');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let open = false;

  if (! reduced) {
    intro();
  }

  // Boven de uitstap hieronder: ook een widget zonder de drie contactknoppen hoort weg
  // te schuiven als er iets anders in dezelfde hoek komt te staan.
  const opeisers = new Set();

  function eis(bron, actief) {
    if (actief) {
      opeisers.add(bron);
    } else {
      opeisers.delete(bron);
    }

    wijk(widget, opeisers.size > 0);
  }

  document.addEventListener('overlay:toggle', (e) => eis('overlay', Boolean(e.detail?.open)));

  const secties = document.querySelectorAll('[data-contact-widget-hide]');

  if (secties.length && typeof IntersectionObserver !== 'undefined') {
    // Al bij de eerste pixel: de sectie is een vol scherm hoog, dus zodra hij binnenkomt
    // staat de contactpersoon erin ook zo in beeld.
    const waarnemer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => eis(entry.target, entry.isIntersecting));
    }, { threshold: 0 });

    secties.forEach((sectie) => waarnemer.observe(sectie));
  }

  if (! toggles.length || ! actions || ! buttons.length) {
    return;
  }

  // Het hidden-attribuut is de vangnetstand voor zonder JavaScript; vanaf hier regelen
  // we de zichtbaarheid zelf.
  actions.hidden = false;
  gsap.set(buttons, { autoAlpha: 0 });
  show(actions, false);

  toggles.forEach((toggle) => toggle.addEventListener('click', swap));

  function intro() {
    gsap.set(widget, { autoAlpha: 0, y: 24 });
    gsap.set(bubbles, { autoAlpha: 0, scale: 0.85, y: 8, transformOrigin: 'right center' });

    if (photo) {
      gsap.set(photo, { autoAlpha: 0, scale: 0.9, transformOrigin: 'right bottom' });
    }

    const timeline = gsap.timeline({ delay: DELAY })
      .to(widget, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out' });

    if (photo) {
      timeline.to(photo, { autoAlpha: 1, scale: 1, duration: 0.45, ease: 'back.out(1.6)' }, '-=0.35');
    }

    timeline.to(bubbles, {
      autoAlpha: 1,
      scale: 1,
      y: 0,
      duration: 0.45,
      ease: 'back.out(1.7)',
      stagger: 0.14,
    }, '-=0.25');
  }

  function swap() {
    open = ! open;

    toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
    show(actions, open);
    show(bubbleWrap, ! open);

    if (reduced) {
      gsap.set(buttons, { autoAlpha: open ? 1 : 0 });
      gsap.set(bubbles, { autoAlpha: open ? 0 : 1 });

      return;
    }

    // Allebei tegelijk laten kruisen: de knoppen komen op terwijl de tekst weggaat. Geen
    // van beide wacht op de ander, dus een klik halverwege laat niets leeg achter.
    gsap.to(open ? bubbles : buttons, {
      autoAlpha: 0,
      scale: open ? 0.9 : 0.6,
      duration: 0.22,
      ease: 'power2.in',
      stagger: { each: 0.05, from: 'end' },
      overwrite: 'auto',
    });

    gsap.fromTo(
      open ? buttons : bubbles,
      { autoAlpha: 0, scale: open ? 0.6 : 0.9, y: open ? 8 : 0 },
      {
        autoAlpha: 1,
        scale: 1,
        y: 0,
        duration: 0.4,
        ease: 'back.out(1.8)',
        stagger: 0.07,
        delay: 0.12,
        overwrite: 'auto',
      }
    );
  }

  /**
   * Buiten beeld zetten zonder uit de stroom te halen: ze liggen over elkaar, dus wat
   * niet aan de beurt is mag geen klikken of tabstops opvangen.
   */
  function show(element, visible) {
    element.style.pointerEvents = visible ? '' : 'none';
    element.setAttribute('aria-hidden', String(! visible));

    element.querySelectorAll('a, button').forEach((child) => {
      child.tabIndex = visible ? 0 : -1;
    });
  }
}
